(() => {
  const script = document.currentScript;
  if (!script) return;

  const giftOffers = {
    welcome: {
      variantId: String(script.dataset.giftVariantId || ''),
      available: script.dataset.giftAvailable === 'true',
      bars: 3,
      bundle: 'welcome_gift',
    },
    promo: {
      variantId: String(script.dataset.promoGiftVariantId || ''),
      available: script.dataset.promoGiftAvailable === 'true',
      bars: 5,
      bundle: 'promo_gift',
    },
  };
  const giftOffer = (code) => /^PROMOGIFT(?:-|$)/i.test(code) ? giftOffers.promo : giftOffers.welcome;
  const giftAvailable = (code) => giftOffer(code).available && Boolean(giftOffer(code).variantId);
  const promotionLabel = (code) => /^PROMOGIFT(?:-|$)/i.test(code)
    ? 'Promo gift - 5 free bars'
    : (/^THANKYOUGIFT(?:-|$)/i.test(code) ? 'Thank you gift - 3 free bars' : 'Welcome gift - 3 free bars');
  const source = 'welcome-gift';
  const welcomeGiftPattern = /^(?:WELCOMEGIFT|THANKYOUGIFT|PROMOGIFT)(?:-[A-Z0-9_-]+)?$/i;
  const sections = ['cart-icon-bubble', 'cart-notification-button'];
  const config = {
    cartUrl: script.dataset.cartUrl,
    cartAddUrl: script.dataset.cartAddUrl,
    cartChangeUrl: script.dataset.cartChangeUrl,
    cartUpdateUrl: script.dataset.cartUpdateUrl,
    checkoutUrl: script.dataset.checkoutUrl,
    checkoutError: script.dataset.checkoutError || 'We could not prepare your cart. Please try again.',
  };

  let activeCode = '';
  let ensureInFlight = null;
  let ensureInFlightCode = '';
  let lastCart = null;
  let lastCartCode = '';
  let checkoutInFlight = false;
  let syncTimeouts = [];

  const readCookieValues = (name) => {
    const prefix = `${name}=`;
    return document.cookie
      .split(';')
      .map((item) => item.trim())
      .filter((item) => item.startsWith(prefix))
      .map((cookie) => {
        try {
          return decodeURIComponent(cookie.slice(prefix.length));
        } catch (error) {
          return cookie.slice(prefix.length);
        }
      });
  };

  const readWelcomeGiftCode = () => {
    for (const cookieValue of readCookieValues('discount_code')) {
      const code = cookieValue
        .split(',')
        .map((item) => item.trim())
        .find((item) => welcomeGiftPattern.test(item));
      if (code) return code;
    }
    return '';
  };

  const expireCookie = (name, domain = '') => {
    const domainAttribute = domain ? `; Domain=${domain}` : '';
    document.cookie = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${domainAttribute}`;
  };

  const deleteWelcomeGiftCookie = () => {
    const hostname = window.location.hostname;
    const parentDomain = hostname.split('.').slice(-2).join('.');
    const domains = ['', hostname, `.${hostname}`, parentDomain, `.${parentDomain}`];
    [...new Set(domains)].forEach((domain) => expireCookie('discount_code', domain));
    return !readWelcomeGiftCode();
  };

  const requestCart = async (url, options = {}, attempt = 0) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
      ...options,
    });
    const data = await response.json().catch(() => null);
    if (response.status === 429 && attempt < 2) {
      const retryAfter = Number(response.headers.get('Retry-After'));
      const delay = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : (attempt + 1) * 500;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      return requestCart(url, options, attempt + 1);
    }
    if (!response.ok || !data) {
      throw new Error((data && (data.description || data.message)) || `Cart request failed (${response.status})`);
    }
    return data;
  };

  const getCart = () => requestCart(config.cartUrl);

  const sectionBody = () => ({
    sections,
    sections_url: window.location.pathname,
  });

  const changeLine = (line, quantity, properties) => {
    const body = { id: line.key, quantity };
    if (properties) body.properties = properties;
    return requestCart(config.cartChangeUrl, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  };

  const applyDiscount = (code) => {
    return requestCart(config.cartUpdateUrl, {
      method: 'POST',
      body: JSON.stringify({ discount: code, ...sectionBody() }),
    });
  };

  const giftLines = (cart) => {
    return (cart.items || []).filter((item) => Object.values(giftOffers).some(
      (offer) => offer.variantId && String(item.variant_id) === offer.variantId
    ));
  };

  const matchingGiftLines = (cart, code) => giftLines(cart).filter(
    (line) => String(line.variant_id) === giftOffer(code).variantId
  );

  const isCompleteCart = (cart) => {
    return Boolean(cart && Array.isArray(cart.items) && Number.isFinite(Number(cart.item_count)));
  };

  const sourceId = (prefix = 'gift') => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return `${prefix}-${window.crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const propertiesForGift = (line, code, components = {}) => {
    const existing = line && line.properties ? line.properties : {};
    const offer = giftOffer(code);
    const preserveSelection = hasCompleteGiftContract(line, code);
    return {
      ...existing,
      _welcome_gift_code: code,
      _welcome_gift_source: 'discount-cookie',
      _ngb_bundle: offer.bundle,
      _ngb_bundle_id: existing._ngb_bundle_id || sourceId(),
      _ngb_dark: preserveSelection ? existing._ngb_dark : String(offer.bars),
      _ngb_dark_macadamia: preserveSelection ? existing._ngb_dark_macadamia : '0',
      _ngb_milk: preserveSelection ? existing._ngb_milk : '0',
      _ngb_milk_almonds: preserveSelection ? existing._ngb_milk_almonds : '0',
      ...components,
    };
  };

  const hasCompleteGiftContract = (line, code) => {
    const properties = (line && line.properties) || {};
    const counts = [
      properties._ngb_dark,
      properties._ngb_dark_macadamia,
      properties._ngb_milk,
      properties._ngb_milk_almonds,
    ].map((value) => Number(value));
    return properties._ngb_bundle === giftOffer(code).bundle
      && Boolean(properties._ngb_bundle_id)
      && counts.every((value) => Number.isInteger(value) && value >= 0)
      && counts.reduce((sum, value) => sum + value, 0) === giftOffer(code).bars;
  };

  const isMatchingFreeGift = (line, code) => {
    if (!line || Number(line.final_line_price) !== 0) return false;
    const normalizedCode = String(code).toUpperCase();
    return (line.line_level_discount_allocations || []).some((allocation) => {
      const application = allocation.discount_application || {};
      return String(application.code || application.title || '').toUpperCase() === normalizedCode;
    });
  };

  const replaceSection = (id, html, selector = '.shopify-section') => {
    if (!html) return;
    const targetRoot = document.getElementById(id);
    if (!targetRoot) return;
    const sourceDocument = new DOMParser().parseFromString(html, 'text/html');
    const sourceElement = sourceDocument.querySelector(selector);
    const targetElement = targetRoot.querySelector(selector) || targetRoot;
    if (sourceElement) targetElement.innerHTML = sourceElement.innerHTML;
  };

  const refreshCartUi = (cart) => {
    if (cart.sections) {
      replaceSection('cart-icon-bubble', cart.sections['cart-icon-bubble']);
      replaceSection('cart-notification-button', cart.sections['cart-notification-button']);
    }

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: cart });
    }

    document.dispatchEvent(new CustomEvent('ngb:welcome-gift-cart-updated', { detail: { cart } }));
  };

  const emitCookieState = (code) => {
    document.dispatchEvent(
      new CustomEvent('ngb:welcome-gift-change', {
        detail: { active: Boolean(code), code: code || '' },
      })
    );
  };

  const removeGiftLines = async (cart) => {
    let currentCart = cart;
    let line = giftLines(currentCart)[0];
    while (line) {
      currentCart = await changeLine(line, 0);
      line = giftLines(currentCart)[0];
    }
    return currentCart;
  };

  const ensureGiftNow = async (code) => {
    if (!giftAvailable(code)) throw new Error('Gift is unavailable');
    const giftVariantId = giftOffer(code).variantId;

    let cart = await getCart();
    let otherGift = giftLines(cart).find((line) => String(line.variant_id) !== giftVariantId);
    while (otherGift) {
      cart = await changeLine(otherGift, 0);
      otherGift = giftLines(cart).find((line) => String(line.variant_id) !== giftVariantId);
    }
    let existingGiftLines = giftLines(cart);
    let primaryLine =
      existingGiftLines.find((line) => {
        const lineCode = line.properties && line.properties._welcome_gift_code;
        return String(lineCode || '').toUpperCase() === String(code).toUpperCase();
      }) || existingGiftLines[0];

    while (primaryLine && existingGiftLines.length > 1) {
      const extraLine = existingGiftLines.find((line) => line.key !== primaryLine.key);
      cart = await changeLine(extraLine, 0);
      existingGiftLines = giftLines(cart);
      primaryLine =
        existingGiftLines.find((line) => {
          const lineCode = line.properties && line.properties._welcome_gift_code;
          return String(lineCode || '').toUpperCase() === String(code).toUpperCase();
        }) || existingGiftLines[0];
    }

    if (!primaryLine) {
      await requestCart(config.cartAddUrl, {
        method: 'POST',
        body: JSON.stringify({
          items: [
            {
              id: Number(giftVariantId),
              quantity: 1,
              properties: propertiesForGift(null, code),
            },
          ],
        }),
      });
    } else {
      const properties = propertiesForGift(primaryLine, code);
      const lineCode = primaryLine.properties && primaryLine.properties._welcome_gift_code;
      const lineSource = primaryLine.properties && primaryLine.properties._welcome_gift_source;
      if (
        Number(primaryLine.quantity) !== 1 ||
        String(lineCode || '').toUpperCase() !== String(code).toUpperCase() ||
        lineSource !== 'discount-cookie' ||
        !hasCompleteGiftContract(primaryLine, code)
      ) {
        cart = await changeLine(primaryLine, 1, properties);
      }
    }

    cart = await applyDiscount(code);
    lastCart = cart;
    lastCartCode = code;
    refreshCartUi(cart);
    return cart;
  };

  const ensureGift = (requestedCode = '', options = {}) => {
    const code = requestedCode || readWelcomeGiftCode();
    if (!welcomeGiftPattern.test(code)) return Promise.reject(new Error('Welcome gift code is missing'));

    activeCode = code;
    if (!options.force && lastCartCode === code && matchingGiftLines(lastCart || {}, code).length === 1) {
      return Promise.resolve(lastCart);
    }
    if (ensureInFlight) {
      if (ensureInFlightCode === code) return ensureInFlight;
      return ensureInFlight.then(() => ensureGift(code, options));
    }

    ensureInFlightCode = code;
    ensureInFlight = ensureGiftNow(code).finally(() => {
      ensureInFlight = null;
      ensureInFlightCode = '';
    });
    return ensureInFlight;
  };

  const updateSelection = async (selection, requestedCode = '', options = {}) => {
    const cleanSelection = String(selection || '').trim();
    if (!cleanSelection) throw new Error('Gift selection is missing');
    const code = requestedCode || activeCode || readWelcomeGiftCode();
    let cart = options.cart || (await getCart());
    let line = matchingGiftLines(cart, code)[0];
    if (!line) {
      cart = await ensureGift(code, { force: true });
      line = matchingGiftLines(cart, code)[0];
    }
    if (!line) throw new Error('Welcome gift was not found in the cart');

    const properties = {
      ...propertiesForGift(line, code, options.components || {}),
      'Gift bar selection': cleanSelection,
      Promotion: promotionLabel(code),
    };
    await changeLine(line, 1, properties);
    cart = await applyDiscount(code);
    const updatedLine = matchingGiftLines(cart, code)[0];
    const expectedComponents = propertiesForGift(updatedLine, code, options.components || {});
    const componentKeys = [
      '_ngb_bundle',
      '_ngb_dark',
      '_ngb_dark_macadamia',
      '_ngb_milk',
      '_ngb_milk_almonds',
    ];
    if (
      !updatedLine ||
      !hasCompleteGiftContract(updatedLine, code) ||
      componentKeys.some((key) => String(updatedLine.properties[key]) !== String(expectedComponents[key]))
    ) {
      throw new Error('Welcome gift composition was not saved');
    }
    lastCart = cart;
    lastCartCode = code;
    refreshCartUi(cart);
    return cart;
  };

  const prepareForCheckout = async (options = {}) => {
    const code = options.code || activeCode || readWelcomeGiftCode();
    let cart = options.cart || (await ensureGift(code, { force: true }));
    const freeGift = matchingGiftLines(cart, code).find((line) => isMatchingFreeGift(line, code));

    if (freeGift) {
      const preserveClaimUntilBundleCheckout = Boolean(document.querySelector('.ngb-byb'));
      deleteWelcomeGiftCookie();
      lastCart = cart;
      if (preserveClaimUntilBundleCheckout) {
        activeCode = code;
        lastCartCode = code;
      } else {
        activeCode = '';
        lastCartCode = '';
        emitCookieState('');
      }
      refreshCartUi(cart);
      return { qualified: true, cart, gift: freeGift };
    }

    if (options.removeIfUnqualified !== false) {
      cart = await removeGiftLines(cart);
      cart = await applyDiscount(options.clearIfUnqualified ? '' : code);
      lastCart = cart;
      if (options.clearIfUnqualified) {
        deleteWelcomeGiftCookie();
        activeCode = '';
        lastCartCode = '';
        emitCookieState('');
      } else {
        lastCartCode = code;
      }
      refreshCartUi(cart);
    }
    return { qualified: false, cart, gift: null };
  };

  const syncFromCookie = () => {
    const nextCode = readWelcomeGiftCode();
    if (nextCode === activeCode) return;
    activeCode = nextCode;
    if (!nextCode) {
      lastCart = null;
      lastCartCode = '';
    }
    emitCookieState(nextCode);
    if (nextCode && giftAvailable(nextCode)) {
      ensureGift(nextCode).catch((error) => console.error('[Welcome gift]', error));
    }
  };

  const scheduleCookieSync = () => {
    syncTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    syncTimeouts = [0, 250, 750, 1500].map((delay) => window.setTimeout(syncFromCookie, delay));
  };

  const checkoutControl = (target) => {
    if (!(target instanceof Element)) return null;
    const control = target.closest('button[name="checkout"], input[name="checkout"], a[href]');
    if (!control) return null;
    if (control.matches('button[name="checkout"], input[name="checkout"]')) return control;
    try {
      const url = new URL(control.href, window.location.origin);
      return /\/checkout\/?$/.test(url.pathname) ? control : null;
    } catch (error) {
      return null;
    }
  };

  const prepareAndNavigate = async (control) => {
    if (checkoutInFlight) return;
    checkoutInFlight = true;
    const wasDisabled = Boolean(control && control.disabled);
    if (control) {
      control.disabled = true;
      control.setAttribute('aria-busy', 'true');
    }

    try {
      await prepareForCheckout({ removeIfUnqualified: true });
      window.location.assign(config.checkoutUrl);
    } catch (error) {
      console.error('[Welcome gift checkout]', error);
      checkoutInFlight = false;
      if (control) {
        control.disabled = wasDisabled;
        control.removeAttribute('aria-busy');
      }
      window.alert(window.cartStrings && window.cartStrings.error ? window.cartStrings.error : config.checkoutError);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const control = checkoutControl(event.target);
      const code = readWelcomeGiftCode();
      if (!control || !code || !giftAvailable(code)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      activeCode = code;
      prepareAndNavigate(control);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const submitter = event.submitter;
      const isCheckoutSubmit =
        submitter && submitter.matches && submitter.matches('button[name="checkout"], input[name="checkout"]');
      const code = readWelcomeGiftCode();
      if (!isCheckoutSubmit || !code || !giftAvailable(code)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      activeCode = code;
      prepareAndNavigate(submitter);
    },
    true
  );

  document.addEventListener('click', scheduleCookieSync, true);
  document.addEventListener('copy', scheduleCookieSync, true);
  window.addEventListener('klaviyoForms', scheduleCookieSync);
  window.addEventListener('pageshow', syncFromCookie);
  window.addEventListener('focus', syncFromCookie);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) syncFromCookie();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event && event.source === source) return;
      const code = readWelcomeGiftCode();
      if (!code || !giftAvailable(code)) return;

      const updatedCart = event && event.cartData;
      const giftWasRemoved =
        isCompleteCart(updatedCart) &&
        giftLines(lastCart || {}).length > 0 &&
        giftLines(updatedCart).length === 0;
      if (giftWasRemoved) {
        deleteWelcomeGiftCookie();
        activeCode = '';
        lastCart = updatedCart;
        lastCartCode = '';
        emitCookieState('');
        return;
      }

      activeCode = code;
      return ensureGift(code, { force: true }).catch((error) => console.error('[Welcome gift cart update]', error));
    });
  }

  window.NGBWelcomeGift = {
    ensureGift,
    getState: () => ({ active: Boolean(activeCode), code: activeCode, enabled: giftAvailable(activeCode) }),
    prepareForCheckout,
    readCode: readWelcomeGiftCode,
    updateSelection,
  };

  syncFromCookie();
})();
